import { Camera, Sunset } from 'lucide-react';
import { useState } from 'react';
import { apiPaths, type SkyResponse } from '../../../shared/api/contract.ts';
import { DEFAULT_CITY, SKY_WEBCAMS } from '../../../shared/cities/index.ts';
import {
  SUNSET_LEVEL_LABELS,
  type DailyIndex,
  type SkyWebcam,
  type SunsetFactor,
  type SunsetLevel,
  type SunsetOutlook,
} from '../../../shared/domain/index.ts';
import { FRESHNESS_POLICIES } from '../../../shared/freshness/index.ts';
import { SectionCard } from '../../components/SectionCard.tsx';
import { useApi } from '../../hooks/useApi.ts';
import { formatTime } from '../../lib/format.ts';
import { describeIndex } from './indices.ts';

// Les libellés viennent du domaine partagé : le Worker écrit le même mot dans le HTML de la page.
const SUNSET_RANKS: Readonly<Record<SunsetLevel, number>> = { dull: 1, modest: 2, good: 3, great: 4 };

const FACTORS: Readonly<Record<SunsetFactor, string>> = {
  clear_sky: 'Ciel sans nuages : coucher net, peu de couleurs dans le ciel',
  high_clouds: 'Voile de nuages d’altitude, prêt à se colorer',
  mid_clouds: 'Nuages moyens pour accrocher la lumière',
  thick_clouds: 'Couche nuageuse épaisse : lumière atténuée',
  horizon_clear: 'Horizon dégagé côté couchant : la lumière passe dessous',
  horizon_blocked: 'Nuages bas côté couchant : le soleil risque d’être masqué',
  low_clouds: 'Nuages bas au-dessus d’Ermont',
  rain: 'Pluie prévue au moment du coucher',
};

const COMPASS = ['le nord', 'le nord-est', 'l’est', 'le sud-est', 'le sud', 'le sud-ouest', 'l’ouest', 'le nord-ouest'];
const towards = (bearingDeg: number) => COMPASS[Math.round(bearingDeg / 45) % 8] ?? 'l’ouest';

function SunsetBlock({ sunset }: { sunset: SunsetOutlook }) {
  const level = { label: SUNSET_LEVEL_LABELS[sunset.level], rank: SUNSET_RANKS[sunset.level] };
  return (
    <div className="sky-sunset">
      <p className="sky-sunset__when">
        Coucher du soleil {sunset.day === 'today' ? 'ce soir' : 'demain'} à{' '}
        <time dateTime={sunset.sunsetAt}>{formatTime(sunset.sunsetAt)}</time>
        {' · heure dorée dès '}
        <time dateTime={sunset.goldenHourAt}>{formatTime(sunset.goldenHourAt)}</time>
      </p>
      <p className="sky-sunset__level">
        Potentiel : <strong>{level.label.toLowerCase()}</strong>
      </p>
      <div className="sky-gauge" aria-hidden="true">
        {[1, 2, 3, 4].map((rank) => (
          <span key={rank} className={`sky-gauge__seg${rank <= level.rank ? ' sky-gauge__seg--on' : ''}`} />
        ))}
      </div>
      {sunset.factors.length > 0 && (
        <ul className="sky-factors">
          {sunset.factors.map((factor) => (
            <li key={factor}>{FACTORS[factor]}</li>
          ))}
        </ul>
      )}
      <details className="sky-method">
        <summary>Comment c’est calculé ?</summary>
        <p>
          Estimation Ermont Live, pas une prévision officielle. Elle compare, à l’heure du coucher, les nuages au-dessus
          d’Ermont (bas {sunset.overhead.lowPct} %, moyens {sunset.overhead.midPct} %, hauts {sunset.overhead.highPct}{' '}
          %) et ceux de l’horizon, à {sunset.horizon.distanceKm} km vers {towards(sunset.horizon.bearingDeg)} (bas{' '}
          {sunset.horizon.lowPct} %, moyens {sunset.horizon.midPct} %).
        </p>
        <p>
          Les plus beaux couchers demandent des nuages d’altitude à colorer au-dessus de nous, et un horizon dégagé pour
          que la lumière rasante passe dessous.
        </p>
      </details>
    </div>
  );
}

/**
 * Webcam tierce chargée seulement à la demande : aucune requête vers Windy tant que la personne n'a rien demandé,
 * et une panne du fournisseur ne ralentit jamais l'accueil.
 */
function SkyWebcamPanel({ webcam }: { webcam: SkyWebcam }) {
  const [open, setOpen] = useState(false);
  const label = `Webcam de ${webcam.place}, ${webcam.facing}`;
  return (
    <div className="sky-webcam">
      <button type="button" className="sky-webcam__toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <Camera size={18} aria-hidden="true" /> {open ? 'Masquer la webcam' : 'Voir le ciel en direct'}
      </button>
      {open && (
        <>
          <iframe
            className="sky-webcam__frame"
            src={webcam.embedUrl}
            title={label}
            referrerPolicy="no-referrer"
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            allow="fullscreen"
          />
          <p className="sky-webcam__note">
            Caméra d’un particulier à {webcam.place}, à {webcam.distanceKm} km {webcam.fromCity} d’Ermont,{' '}
            {webcam.facing}. {webcam.caveat}{' '}
            <a href={webcam.pageUrl} rel="noopener noreferrer" target="_blank">
              Voir la webcam sur Windy
            </a>{' '}
            ·{' '}
            <a href={webcam.credit.url} rel="noopener noreferrer" target="_blank">
              {webcam.credit.text}
            </a>
          </p>
        </>
      )}
    </div>
  );
}

/** Tuile d'indice dans la grille des indices du jour. */
export function IndexTile({ index }: { index: DailyIndex }) {
  const described = describeIndex(index);
  if (described === null) return null;
  const { icon: Icon, label, value, detail } = described;
  return (
    <div className="now-item">
      <Icon size={22} aria-hidden="true" />
      <div>
        <dt>{label}</dt>
        <dd>
          {value}
          <span className="now-item__detail">{detail}</span>
        </dd>
      </div>
    </div>
  );
}

export function SkyCard() {
  const sky = useApi<SkyResponse>(apiPaths.sky(DEFAULT_CITY.slug), FRESHNESS_POLICIES.sky.refreshMs);
  const webcam = SKY_WEBCAMS[DEFAULT_CITY.slug];
  return (
    <SectionCard
      title="Coucher de soleil et indices du jour"
      icon={<Sunset size={20} aria-hidden="true" />}
      kind="sky"
      section={sky.data?.sky}
      loading={sky.loading}
      fetchError={sky.error}
    >
      {(outlook) => {
        // Filtrés AVANT la grille : la règle « nombre impair → dernier sur toute la ligne » compte les tuiles affichées.
        const known = outlook.indices.filter((index) => describeIndex(index) !== null);
        return (
          <>
            {outlook.sunset === null ? (
              <p className="state-message">Estimation du coucher de soleil indisponible.</p>
            ) : (
              <SunsetBlock sunset={outlook.sunset} />
            )}
            {known.length > 0 && (
              <dl className="now-grid sky-indices">
                {known.map((index) => (
                  <IndexTile key={index.kind} index={index} />
                ))}
              </dl>
            )}
            {webcam !== undefined && <SkyWebcamPanel webcam={webcam} />}
          </>
        );
      }}
    </SectionCard>
  );
}
